import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Header.css';
import { LOGO_URL } from '../../services';
import { NavLink, useNavigate } from 'react-router-dom';
import { TbBell, TbChevronDown, TbTrash, TbUser } from "react-icons/tb";
import { RxHamburgerMenu } from 'react-icons/rx';
import { useAuth } from '../../AuthContext';
import { NOTIFICATION_TYPES, getNotificationTypeConfig } from "../../utils/notificationTypes";
import Services from '../../services/Services';
import { includesSearchText, normalizeSearchText } from '../../utils/searchText';
import { getHomepageRedirectPath } from '../../utils/homepage';
import HeaderStoreActions from '../../pages/StorePage/components/HeaderStoreActions';
import { klavyeIleTikla } from "../../utils/a11y"; // FE-78: tıklanabilir öğelerde klavye desteği
import { oturumuKapat } from "../../utils/oturum";

const isNotificationUnread = (notification) => {
  if (!notification || typeof notification !== "object") return false;
  const maybeRead =
    notification.isRead ??
    notification.read ??
    notification.seen ??
    notification.isSeen ??
    notification.is_seen ??
    notification.readAt ??
    notification.read_at;

  if (typeof maybeRead === "boolean") return !maybeRead;
  if (typeof maybeRead === "number") return maybeRead === 0;
  if (typeof maybeRead === "string") return maybeRead === "0" || maybeRead.toLowerCase() === "false";
  return true;
};

const getUnreadCount = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.filter((n) => isNotificationUnread(n)).length;
};

const getNotificationIdentity = (notification, index = 0) => {
  if (!notification || typeof notification !== "object") return `notification-${index}`;
  const explicitId = notification.notificationID ?? notification.notificationId ?? notification.id;
  if (explicitId !== undefined && explicitId !== null) return `id:${explicitId}`;

  const data = getNotificationData(notification);
  const type = data?.type ?? notification?.type ?? "";
  const content = notification?.content ?? notification?.message ?? notification?.title ?? "";
  const createdAt =
    notification?.createdAt ??
    notification?.created_at ??
    notification?.date ??
    notification?.time ??
    notification?.notificationDate ??
    "";

  return `fallback:${type}:${createdAt}:${content}:${index}`;
};

// Bildirimin saatini "12:21" biçiminde döndürür. Backend createdDateTime'ı saniye cinsinden
// unix timestamp olarak gönderir (bkz. notifications.php get()); ms gelirse de desteklenir.
// Tarih okunamazsa boş string döner → saat hiç render edilmez.
const formatNotificationTime = (notification) => {
  const raw =
    notification?.createdDateTime ??
    notification?.created_date_time ??
    notification?.createdAt ??
    notification?.created_at ??
    null;
  if (raw === null || raw === undefined || raw === "") return "";

  const numeric = Number(raw);
  const ms = Number.isFinite(numeric)
    ? (numeric < 1e12 ? numeric * 1000 : numeric) // saniye → ms
    : Date.parse(raw);
  if (!Number.isFinite(ms)) return "";

  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

const NOTIFICATION_FAVICON_PATH = `${process.env.PUBLIC_URL || ""}/troy-favicon.png`;
let faviconObjectUrl = null;
let faviconBaseImagePromise = null;
let faviconUpdateSequence = 0;
let notificationAudioContext = null;

const getFaviconLink = () => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  return link;
};

const loadFaviconBaseImage = () => {
  if (faviconBaseImagePromise) return faviconBaseImagePromise;

  faviconBaseImagePromise = new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = NOTIFICATION_FAVICON_PATH;
  });

  return faviconBaseImagePromise;
};

const updateFaviconBadge = async (count) => {
  if (typeof document === "undefined") return;

  const updateSequence = ++faviconUpdateSequence;
  const link = getFaviconLink();
  const unreadCount = Number(count) || 0;

  if (unreadCount <= 0) {
    link.href = NOTIFICATION_FAVICON_PATH;
    if (faviconObjectUrl) {
      URL.revokeObjectURL(faviconObjectUrl);
      faviconObjectUrl = null;
    }
    return;
  }

  try {
    const image = await loadFaviconBaseImage();
    if (updateSequence !== faviconUpdateSequence) return;

    const canvas = document.createElement("canvas");
    const size = 64;
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.clearRect(0, 0, size, size);
    context.drawImage(image, 0, 0, size, size);

    const label = unreadCount > 99 ? "99+" : String(unreadCount);
    const badgeWidth = label.length > 2 ? 54 : 46;
    const badgeHeight = 38;
    const badgeX = size - badgeWidth;
    const badgeY = size - badgeHeight;
    const radius = badgeHeight / 2;

    context.fillStyle = "#ef4444";
    context.beginPath();
    context.moveTo(badgeX + radius, badgeY);
    context.lineTo(badgeX + badgeWidth - radius, badgeY);
    context.quadraticCurveTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + radius);
    context.lineTo(badgeX + badgeWidth, badgeY + badgeHeight - radius);
    context.quadraticCurveTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX + badgeWidth - radius, badgeY + badgeHeight);
    context.lineTo(badgeX + radius, badgeY + badgeHeight);
    context.quadraticCurveTo(badgeX, badgeY + badgeHeight, badgeX, badgeY + badgeHeight - radius);
    context.lineTo(badgeX, badgeY + radius);
    context.quadraticCurveTo(badgeX, badgeY, badgeX + radius, badgeY);
    context.fill();

    context.fillStyle = "#ffffff";
    context.font = `800 ${label.length > 2 ? 30 : 34}px Arial, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(label, badgeX + badgeWidth / 2, badgeY + badgeHeight / 2 + 1);

    canvas.toBlob((blob) => {
      if (updateSequence !== faviconUpdateSequence) return;
      if (!blob) return;
      if (faviconObjectUrl) URL.revokeObjectURL(faviconObjectUrl);
      faviconObjectUrl = URL.createObjectURL(blob);
      link.href = faviconObjectUrl;
    }, "image/png");
  } catch {
    link.href = NOTIFICATION_FAVICON_PATH;
  }
};

const getNotificationAudioContext = () => {
  if (typeof window === "undefined") return;

  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  if (!notificationAudioContext || notificationAudioContext.state === "closed") {
    notificationAudioContext = new AudioContext();
  }

  return notificationAudioContext;
};

const unlockNotificationSound = () => {
  try {
    const context = getNotificationAudioContext();
    if (context?.state === "suspended") {
      context.resume().catch(() => {});
    }
  } catch {
    // Browsers can block sound until the user interacts with the page.
  }
};

const playNotificationSound = () => {
  try {
    const context = getNotificationAudioContext();
    if (!context) return;

    const play = () => {
      if (context.state !== "running") return;

      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const startedAt = context.currentTime;

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, startedAt);
      oscillator.frequency.exponentialRampToValueAtTime(660, startedAt + 0.16);

      gain.gain.setValueAtTime(0.0001, startedAt);
      gain.gain.exponentialRampToValueAtTime(0.18, startedAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startedAt + 0.22);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startedAt);
      oscillator.stop(startedAt + 0.24);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
      };
    };

    if (context.state === "suspended") {
      context.resume().then(play).catch(() => {});
      return;
    }

    play();
  } catch {
    // Browsers can block sound until the user interacts with the page.
  }
};

const closeNotificationSound = () => {
  try {
    if (notificationAudioContext && notificationAudioContext.state !== "closed") {
      notificationAudioContext.close();
    }
    notificationAudioContext = null;
  } catch {
    notificationAudioContext = null;
  }
};

const COMPANY_LIST_NOTIFICATION_TYPES = new Set([
  "add_warehouse_bank_account",
  "upload_warehouse_document",
  "upload_warehouse_agreement",
]);

const WAREHOUSE_LIST_NOTIFICATION_TYPES = new Set([
  "created_new_warehouse",
  "update_warehouse",
  "add_warehouse_room",
  "update_your_price_list",
  "warehouse_approved_price_required",
]);

const WAREHOUSE_PRICE_NOTIFICATION_TYPES = new Set([
  "add_warehouse_room",
  "update_your_price_list",
  "warehouse_approved_price_required",
]);

// Sözleşme slug'ları TEK kaynaktan gelir: utils/notificationTypes.js
// (eski/hiç üretilmeyen "contract_ready, sign_contract…" adları için geriye dönük destek korunur).
const CONTRACT_NOTIFICATION_TYPES = new Set([
  ...Object.keys(NOTIFICATION_TYPES).filter((slug) => NOTIFICATION_TYPES[slug]?.category === "contract"),
  "contract",
  "contract_ready",
  "contract_to_sign",
  "customer_contract_created",
  "customer_contract_signed",
  "sign_contract",
]);

const OFFER_PRICE_REQUEST_NOTIFICATION_TYPES = new Set([
  "offer_price_request",
]);

const NEW_OFFER_CREATED_NOTIFICATION_TYPES = new Set([
  "new_offer_created",
  // Admin bir talebe teklif oluşturdu/gönderdi (backend: admin_offer_flow) — merchant teklif
  // bildirimiyle aynı yere, /admin-requests talep detayına yönlenir.
  "admin_offer_created",
  "admin_offer_sent",
]);

const REQUEST_DETAIL_NOTIFICATION_TYPES = new Set([
  "new_request_created",
  // Admin müşteri adına talep oluşturduğunda diğer adminlere gelir -> Talepler sayfasında talebi aç
  "admin_created_request",
]);

// Admin müşteri adına talep oluşturduğunda ilgili müşteriye gelir -> Taleplerim sayfasında talebi aç
const MY_REQUEST_DETAIL_NOTIFICATION_TYPES = new Set([
  "request_created_for_you",
]);

const CUSTOMER_CONFIRMED_OFFER_NOTIFICATION_TYPES = new Set([
  "customer_confirmed_offer",
  // Admin teklif onayladığında onaylayan hariç diğer adminlere gelir -> ilgili teklifi aç (Görüntüle)
  "admin_confirmed_offer",
]);

const OPERATION_NOTIFICATION_TYPES = new Set([
  "operation_planned",
  "operation_incomplete",
]);

// Müşteri kendi depo çıkışını oluşturduğunda tüm adminlere, admin müşteri adına depo
// çıkışı oluşturduğunda oluşturan hariç diğer adminlere gelir -> Operasyonlar sayfasında
// müşteri filtrelenip ilgili operasyon modalı açılsın
const WAREHOUSE_EXIT_NOTIFICATION_TYPES = new Set([
  "warehouse_exit_created",
  "admin_created_warehouse_exit",
]);

// Admin müşteri adına depo çıkışı oluşturduğunda ilgili müşteriye gelir
// -> Operasyonlarım sayfasında ilgili operasyon modalı açılsın
const MY_WAREHOUSE_EXIT_NOTIFICATION_TYPES = new Set([
  "warehouse_exit_created_for_you",
]);

const OFFER_SENT_NOTIFICATION_TYPES = new Set([
  "offer_sent",
]);

const COMPANY_APPROVAL_NOTIFICATION_TYPES = new Set([
  "company_document_approved",
  "company_document_rejected",
  "company_agreement_approved",
  // Firma imzası / kaşesi: yükleme ve onay-red bildirimleri firma profiline götürür.
  "company_mark_uploaded",
  "company_mark_approved",
  "company_mark_rejected",
]);

const COMPANY_MARK_NOTIFICATION_TYPES = new Set([
  "company_mark_uploaded",
  "company_mark_approved",
  "company_mark_rejected",
]);

const getNotificationData = (notification) => {
  const rawData = notification?.data ?? notification?.notificationData;
  if (!rawData) return {};
  if (typeof rawData === "string") {
    try {
      const parsed = JSON.parse(rawData);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof rawData === "object" ? rawData : {};
};

const isContractNotificationType = (type) => {
  const normalized = normalizeSearchText(type, { compact: true });
  return (
    CONTRACT_NOTIFICATION_TYPES.has(String(type || "")) ||
    normalized.includes("contract") ||
    normalized.includes("sozlesme")
  );
};

const isContractNotificationText = (text) => {
  const normalized = normalizeSearchText(text, { compact: true });
  return normalized.includes("contract") || normalized.includes("sozlesme");
};

const getNotificationNavigationTarget = (notification, userType) => {
  const data = getNotificationData(notification);
  const type = data?.type ?? notification?.type;
  const typeKey = String(type || "");
  const contractID = data?.contractID ?? data?.contractId ?? data?.contract_id ?? notification?.contractID;
  const operationID = data?.operationID ?? data?.operationId ?? data?.operation_id ?? notification?.operationID;
  const requestID = data?.requestID ?? data?.requestId ?? data?.request_id ?? notification?.requestID;
  const offerID = data?.offerID ?? data?.offerId ?? data?.offer_id ?? notification?.offerID;
  const fileID = data?.fileID ?? data?.fileId ?? data?.file_id ?? notification?.fileID;
  const companyID =
    data?.companyID ??
    data?.companyId ??
    data?.company_id ??
    data?.corporationID ??
    data?.corporationId ??
    data?.corporation_id ??
    notification?.companyID;
  const warehouseID = data?.warehouseID ?? data?.warehouseId ?? data?.warehouse_id ?? notification?.warehouseID;
  const content = notification?.content ?? notification?.message ?? notification?.title;
  const customerID =
    data?.customerID ??
    data?.customerId ??
    data?.customer_id ??
    data?.userID ??
    data?.userId ??
    notification?.customerID ??
    notification?.userID;

  // Ödeme / fatura bildirimleri: yönlendirme merkezi slug haritasından çözülür.
  const mappedType = getNotificationTypeConfig(typeKey);
  if (mappedType && mappedType.category !== "contract" && typeof mappedType.route === "function") {
    const mappedTarget = mappedType.route(userType, data);
    if (mappedTarget) return mappedTarget;
  }

  const isContractNotification = !COMPANY_LIST_NOTIFICATION_TYPES.has(typeKey) &&
    (isContractNotificationType(type) || contractID || isContractNotificationText(content));

  if (!type && !contractID && !operationID && !isContractNotification) return null;

  if (REQUEST_DETAIL_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (requestID) params.set("viewRequestId", String(requestID));

    const basePath = customerID
      ? `/admin-requests/${customerID}`
      : "/admin-requests";

    return {
      path: `${basePath}${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(requestID ? { openRequestID: requestID } : {}),
      },
    };
  }

  if (MY_REQUEST_DETAIL_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (requestID) params.set("viewRequestId", String(requestID));

    return {
      path: `/my-requests${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(requestID ? { openRequestID: requestID } : {}),
      },
    };
  }

  if (CUSTOMER_CONFIRMED_OFFER_NOTIFICATION_TYPES.has(typeKey)) {
    // Partner de alır (BE: offers::notifyCompanyOfferConfirmed). /admin-offers admin sayfasıdır.
    // Depolamada onayla oluşan OPERASYON açılır (data.operationID); nakliyede, red bildirimindeki
    // gibi ilgili talep.
    if (userType === "merchant") {
      if (operationID) {
        return {
          path: `/my-operations?viewOperationId=${encodeURIComponent(String(operationID))}`,
          state: { openOperationID: operationID, autoOpenViewOperation: true },
        };
      }
      return requestID
        ? { path: `/transport-requests/${encodeURIComponent(String(requestID))}` }
        : { path: "/transport-requests" };
    }
    const params = new URLSearchParams();
    if (requestID) params.set("requestID", String(requestID));
    if (offerID) params.set("offerID", String(offerID));
    params.set("autoOpenOfferModal", "1");

    return {
      path: `/admin-offers${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(requestID ? { openRequestID: requestID } : {}),
        ...(offerID ? { openOfferID: offerID } : {}),
        autoOpenOfferModal: true,
      },
    };
  }

  if (OPERATION_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (typeKey === "operation_incomplete") params.set("action", "complete");
    const operationPath = operationID
      ? `/my-operations/${encodeURIComponent(String(operationID))}`
      : "/my-operations";

    return {
      path: `${operationPath}${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(operationID ? { openOperationID: operationID } : {}),
        autoOpenCompleteOperation: typeKey === "operation_incomplete",
      },
    };
  }

  if (WAREHOUSE_EXIT_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (customerID) params.set("userID", String(customerID));
    if (operationID) params.set("viewOperationId", String(operationID));

    return {
      path: `/my-operations${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(operationID ? { openOperationID: operationID } : {}),
        autoOpenViewOperation: true,
      },
    };
  }

  if (MY_WAREHOUSE_EXIT_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (operationID) params.set("viewOperationId", String(operationID));

    return {
      path: `/my-operations${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(operationID ? { openOperationID: operationID } : {}),
        autoOpenViewOperation: true,
      },
    };
  }

  if (OFFER_SENT_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (offerID) {
      params.set("offerID", String(offerID));
      params.set("autoOpenOfferModal", "1");
    } else if (requestID) {
      params.set("requestID", String(requestID));
      params.set("autoOpenPreferredOffer", "1");
    }

    return { path: `/my-offers${params.toString() ? `?${params.toString()}` : ""}` };
  }

  if (COMPANY_APPROVAL_NOTIFICATION_TYPES.has(typeKey)) {
    const openCompanyTab = COMPANY_MARK_NOTIFICATION_TYPES.has(typeKey)
      ? "profile"
      : (typeKey === "company_document_approved" || typeKey === "company_document_rejected" ? "documents" : "contracts");
    const state = {
      openCompanyTab,
      initialTab: openCompanyTab,
      ...(fileID ? { openFileID: fileID } : {}),
    };

    if (userType === "merchant") {
      return {
        path: `/my-warehouse-firm?tab=${encodeURIComponent(openCompanyTab)}`,
        state,
      };
    }

    return companyID
      ? { path: "/firm-list", state: { ...state, openCompanyID: companyID } }
      : { path: "/firm-list", state };
  }

  if (OFFER_PRICE_REQUEST_NOTIFICATION_TYPES.has(typeKey)) {
    return {
      path: requestID ? `/transport-requests/${encodeURIComponent(String(requestID))}` : "/transport-requests",
      state: {
        ...(requestID ? { openRequestID: requestID } : {}),
        autoOpenBid: true,
      },
    };
  }

  if (NEW_OFFER_CREATED_NOTIFICATION_TYPES.has(typeKey)) {
    const params = new URLSearchParams();
    if (requestID) params.set("requestID", String(requestID));
    params.set("autoOpenTransportCompanyOffers", "1");

    return {
      path: `/admin-requests${params.toString() ? `?${params.toString()}` : ""}`,
      state: {
        ...(requestID ? { openRequestID: requestID } : {}),
        ...(data?.offerID ? { openOfferID: data.offerID } : {}),
        autoOpenTransportCompanyOffers: true,
      },
    };
  }

  if (isContractNotification) {
    const state = {
      ...(contractID ? { openContractID: contractID } : {}),
      ...(operationID ? { openOperationID: operationID } : {}),
      ...(data?.customerName ? { customerName: data.customerName } : {}),
    };

    if (userType === "customer") {
      return {
        path: operationID ? `/my-contracts/${encodeURIComponent(String(operationID))}` : "/my-contracts",
        state,
      };
    }
    // Admin / merchant: bildirimdeki müşterinin sözleşmelerine filtreli git.
    if (customerID) {
      return {
        path: `/customer-contracts/${encodeURIComponent(String(customerID))}`,
        state,
      };
    }
    // customerID yoksa (eski bildirimler) genel Sözleşmeler sayfası — /my-contracts müşteri
    // sayfası olduğu için admin/merchant orada Müşteriler'e atılıyordu.
    return { path: "/contracts", state };
  }

  if (COMPANY_LIST_NOTIFICATION_TYPES.has(typeKey)) {
    const openCompanyTab =
      typeKey === "add_warehouse_bank_account"
        ? "bankInformations"
        : typeKey === "upload_warehouse_document"
          ? "documents"
          : typeKey === "upload_warehouse_agreement"
            ? "contracts"
            : null;

    return companyID
      ? { path: "/firm-list", state: { openCompanyID: companyID, openCompanyTab } }
      : { path: "/firm-list" };
  }

  if (warehouseID) {
    const openWarehouseView = WAREHOUSE_PRICE_NOTIFICATION_TYPES.has(typeKey) ? "price" : "details";
    return {
      path: "/warehouse-list",
      state: { openWarehouseID: warehouseID, openWarehouseView },
    };
  }

  if (WAREHOUSE_LIST_NOTIFICATION_TYPES.has(typeKey)) return { path: "/warehouse-list" };

  // Fallbacks (typlar genişleyebilir): ID varsa ilgili listeye git
  if (companyID) return { path: "/firm-list", state: { openCompanyID: companyID } };
  return null;
};

const Header = ({ toggleSidebar }) => {
  const { userType, logout, merchantRole, merchantCompanyTypes, customerHasWarehouseEntry } = useAuth();
  const token = localStorage.getItem('token');

  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsShowAll, setNotificationsShowAll] = useState(false);
  const [notificationSearch, setNotificationSearch] = useState("");
  const [firmData, setFirmData] = useState();
  const menuRef = useRef(null);
  const notificationsRef = useRef(null);
  const notificationsInFlightRef = useRef(false);
  const notificationSnapshotInitializedRef = useRef(false);
  const notificationIdsRef = useRef(new Set());

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [fullName, setFullName] = useState(() => {
    const n = localStorage.getItem("name") || "";
    const l = localStorage.getItem("lastname") || "";
    return (n + " " + l).trim();
  });
  const [avatarUrl, setAvatarUrl] = useState("");
  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
        setNotificationsShowAll(false);
        setNotificationSearch("");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    // 🔸 ProfilePage'den gelen güncellemeyi yakala
    const handleProfileUpdated = (e) => {
      const { name = localStorage.getItem("name") || "", lastname = localStorage.getItem("lastname") || "" } = e.detail || {};
      setFullName((name + " " + lastname).trim());
      if (Object.prototype.hasOwnProperty.call(e.detail || {}, "avatar")) {
        setAvatarUrl(e.detail?.avatar || "");
      }
    };
    window.addEventListener("profile:updated", handleProfileUpdated);

    // 🔸 Sekme odağı değişince (ör: profil sayfasından geri dön), LS’yi tazele
    const syncOnFocus = () => {
      const n = localStorage.getItem("name") || "";
      const l = localStorage.getItem("lastname") || "";
      setFullName((n + " " + l).trim());
    };
    window.addEventListener("focus", syncOnFocus);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener("profile:updated", handleProfileUpdated);
      window.removeEventListener("focus", syncOnFocus);
    };
  }, []);

  const closeNotifications = useCallback(() => {
    setNotificationsOpen(false);
    setNotificationsShowAll(false);
    setNotificationSearch("");
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        closeNotifications();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeNotifications]);


  const handleRedirectToProfilePage = () => {
    navigate('/profile')
    setMenuOpen(false);
  }

  const handleLogout = () => {
    // Token/ad/soyad/userID/vade temizlenir + token sunucuda iptal edilir (utils/oturum.js).
    oturumuKapat();
    logout?.();
    navigate("/", { replace: true })
  }

  const getCompany = useCallback((companyID) => {
    if (!companyID) return;
    Services.GetWarehouseCompany(token, companyID)
      .then((res) => {
        if (res?.data?.status) {
          setFirmData(res.data.result?.[0]);
        }
      })
  }, [token]);

  const getUserInfos = useCallback(() => {
    Services.GetUserInfos(token)
      .then((res) => {
        if (res?.data?.status) {
          const info = Array.isArray(res.data.result) ? res.data.result?.[0] : res.data.result;
          getCompany(info?.corporationID);
          setAvatarUrl(info?.avatar || "");
        }
      })
  }, [getCompany, token]);

  useEffect(() => {
    getUserInfos()
  }, [getUserInfos]);

  useEffect(() => {
    updateFaviconBadge(unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    const handleUserGesture = () => {
      unlockNotificationSound();
    };

    window.addEventListener("pointerdown", handleUserGesture);
    window.addEventListener("keydown", handleUserGesture);
    window.addEventListener("touchstart", handleUserGesture, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
      window.removeEventListener("touchstart", handleUserGesture);
      updateFaviconBadge(0);
      closeNotificationSound();
    };
  }, []);

  const fetchUserNotifications = useCallback(async () => {
    if (!token) return;
    if (notificationsInFlightRef.current) return;
    notificationsInFlightRef.current = true;

    try {
      const res = await Services.GetUserNotifications(token);
      if (res?.data?.status) {
        const items = res.data.result ?? [];
        const nextNotificationIds = new Set(items.map((item, index) => getNotificationIdentity(item, index)));
        const hasNewUnreadNotification =
          notificationSnapshotInitializedRef.current &&
          items.some((item, index) => {
            const identity = getNotificationIdentity(item, index);
            return !notificationIdsRef.current.has(identity) && isNotificationUnread(item);
          });

        notificationIdsRef.current = nextNotificationIds;
        notificationSnapshotInitializedRef.current = true;

        setNotifications(items);
        const nextUnreadCount = getUnreadCount(items);
        setUnreadCount(nextUnreadCount);
        window.dispatchEvent(new CustomEvent("notifications:updated", { detail: { items, unreadCount: nextUnreadCount } }));

        if (hasNewUnreadNotification) {
          playNotificationSound();
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
    } finally {
      notificationsInFlightRef.current = false;
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;

    fetchUserNotifications();
    // Bildirimler 3 dakikada bir çekilir (15 sn gereksiz yük oluşturuyordu).
    const intervalId = window.setInterval(fetchUserNotifications, 180000);

    return () => {
      window.clearInterval(intervalId);
      notificationsInFlightRef.current = false;
    };
  }, [fetchUserNotifications, token]);

  const handleToggleSidebar = () => {
    setMenuOpen(false);
    closeNotifications();
    toggleSidebar?.();
  };

  const toggleNotifications = () => {
    setMenuOpen(false);
    if (notificationsOpen) {
      closeNotifications();
      return;
    }
    setNotificationsOpen(true);
    fetchUserNotifications();
  };

  const updateNotificationsAsReadLocally = (notificationID) => {
    if (!notificationID) return;
    setNotifications((prev) => {
      const next = prev.map((n) => {
        if (!n) return n;
        const id = n.notificationID ?? n.id ?? n.notificationId;
        if (String(id) !== String(notificationID)) return n;
        return { ...n, isRead: "1" };
      });
      setUnreadCount(getUnreadCount(next));
      return next;
    });
  };

  const handleMarkAllNotificationsAsRead = async () => {
    if (!token || unreadCount === 0) return;

    setNotifications((prev) => {
      const next = prev.map((n) => (n ? { ...n, isRead: "1" } : n));
      window.dispatchEvent(new CustomEvent("notifications:updated", { detail: { items: next, unreadCount: 0 } }));
      return next;
    });
    setUnreadCount(0);

    try {
      await Services.SetAllUserNotificationsAsRead(token);
    } catch (err) {
      console.error("Bildirimler okundu olarak işaretlenemedi:", err);
    }
  };

  const handleNotificationClick = async (notification) => {
    const notificationID = notification?.notificationID ?? notification?.id ?? notification?.notificationId;
    const target = getNotificationNavigationTarget(notification, userType);
    const notificationNavigationId = `${notificationID || "notification"}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    updateNotificationsAsReadLocally(notificationID);
    if (token && notificationID) {
      try {
        await Services.SetUserNotificationAsRead(token, notificationID);
      } catch (err) {
        console.error("Bildirim okundu olarak işaretlenemedi:", err);
      }
    }

    closeNotifications();
    if (target?.path) {
      navigate(target.path, {
        state: {
          ...(target.state || {}),
          notificationNavigationId,
        },
      });
    }
  };

  const normalizedSearch = normalizeSearchText(notificationSearch);
  const searchActive = normalizedSearch.length > 0;
  const baseList = notificationsShowAll || searchActive ? notifications : notifications.slice(0, 5);
  const visibleNotifications = !searchActive
    ? baseList
    : baseList.filter((n) => includesSearchText(n?.content, normalizedSearch));
  const homePath =
    getHomepageRedirectPath({
      userType,
      merchantRole,
      merchantCompanyTypes,
      customerHasWarehouseEntry,
    }) || "/homepage";
  const userAuthLabel = userType === "customer" || userType === "admin" ? "" : firmData?.title;

  return (
    <header className="header">
      <NavLink
        className='logo-holder'
        to={homePath}
        onClick={() => {
          setMenuOpen(false);
          closeNotifications();
        }}
      >
        <img src={LOGO_URL} alt='logo' />
      </NavLink>

      <span className="header-actions">

        <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla} onClick={handleToggleSidebar} className='header-burger-menu'><RxHamburgerMenu /></span>


        <div
          className="header-bell-holder"
          ref={notificationsRef}
        >
          <button
            type="button"
            className="header-bell-button"
            onClick={toggleNotifications}
            aria-label={
              unreadCount
                ? `${unreadCount} okunmamış / ${notifications.length} toplam bildirim`
                : "Bildirimler"
            }
            title={
              unreadCount
                ? `${unreadCount} okunmamış / ${notifications.length} toplam bildirim`
                : "Bildirimler"
            }
          >
            <TbBell className='header-bell-icon' size={16} strokeWidth={1.5} />
            {unreadCount > 0 && <span className="header-bell-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </button>

          {notificationsOpen && (
            <div className="header-notifications-dropdown" onMouseDown={(e) => e.stopPropagation()}>
              <div className="header-notifications-title-row">
                <div className="header-notifications-title">Bildirimler</div>
                {unreadCount > 0 ? (
                  <button
                    type="button"
                    className="header-notifications-mark-all-read"
                    onClick={handleMarkAllNotificationsAsRead}
                  >
                    Tümünü okundu olarak işaretle
                  </button>
                ) : null}
              </div>

              <input
                className="header-notifications-search"
                type="text"
                placeholder="Bildirimlerde ara..."
                value={notificationSearch}
                onChange={(e) => setNotificationSearch(e.target.value)}
              />

              <div className="header-notifications-list">
                {visibleNotifications.length === 0 ? (
                  <div className="header-notifications-empty">Bildirim bulunamadı</div>
                ) : (
                  visibleNotifications.map((n, index) => {
                    const id = n?.notificationID ?? n?.id ?? n?.notificationId ?? `notification-${index}`;
                    const unread = isNotificationUnread(n);
                    const time = formatNotificationTime(n);
                    return (
                      <button
                        type="button"
                        key={id}
                        className={`header-notification-item ${unread ? "unread" : "read"}`}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <span className="header-notification-content">{n?.content}</span>
                        <span className="header-notification-meta">
                          {unread ? <span className="header-notification-dot" /> : null}
                          {time ? <span className="header-notification-time">{time}</span> : null}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              {!notificationsShowAll && !searchActive && notifications.length > 5 ? (
                <button
                  type="button"
                  className="header-notifications-view-all"
                  onClick={() => setNotificationsShowAll(true)}
                >
                  Tümünü gör
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Mağaza: sepet + İlan Ver — bildirimlerin sağında, profilin solunda (yalnız mağazaya
            erişen roller: müşteri, merchant). */}
        {(userType === "customer" || userType === "merchant") && <HeaderStoreActions />}

        <div className='notification-user-holder' ref={menuRef}>
          <div
            className='header-user-holder-avatar'
            style={{
              backgroundImage: avatarUrl ? `url(${avatarUrl})` : "none",
            }}
          />

          <div className='header-user-holder-name-auth-holder'>
            <span
              className='header-user-holder-name header-user-holder-name-clickable'
              role="button"
              tabIndex={0}
              onClick={handleRedirectToProfilePage}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleRedirectToProfilePage();
              }}
            >
              {fullName}
            </span>
            {userAuthLabel ? <span className='header-user-holder-auth'>{userAuthLabel}</span> : null}
          </div>

          <TbChevronDown
            className='header-user-holder-arrow'
            onClick={toggleMenu}
          />

          {menuOpen && (
            <div className="header-dropdown-menu">
              <ul>
                <li role="button" tabIndex={0} onKeyDown={klavyeIleTikla} onClick={handleRedirectToProfilePage}><TbUser size={15} color='rgba(188, 193, 201, 1)' /> Profilim</li>
                {/* <li><TbKey size={15} color='rgba(188, 193, 201, 1)' /> Şifreler</li> */}
                <li role="button" tabIndex={0} onKeyDown={klavyeIleTikla} onClick={handleLogout}><TbTrash size={15} color='rgba(188, 193, 201, 1)' /> Çıkış Yap</li>
              </ul>
              {/* Uygulama sürümü artık sidebar'ın en altında gösteriliyor (Sidebar.js). */}
            </div>
          )}
        </div>
      </span>
    </header>
  );
};

export default Header;
